import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateSharePermission,
  User_InvitePortalClientMutation,
  useUser_InvitePortalClientMutation,
} from 'queries';
import * as yup from 'yup';

import Chip, { ChipDefaultColor } from 'common/chip';
import FormSelectField from 'common/form-select-field';
import Modal from 'common/modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  content: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
  },
  title: {
    fontWeight: 700,
    fontSize: '24px',
    marginTop: '18px',
    alignSelf: 'center',
  },
  selectContainer: {
    marginTop: '32px',
  },
  selectInput: {
    borderRadius: '8px',
    '& fieldset': {
      border: '1px solid #ECEEF1',
    },
  },
  shareButton: {
    borderRadius: '100px',
    maxHeight: '40px',
    marginTop: '40px',
    marginBottom: '20px',
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
  },
  comingSoonBadge: {
    marginTop: '10px',
    float: 'left',
  },
}));

type Props = {
  estateId: string;
  estateName: string;
  clientEmailAddress: string;
  title?: string;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  onExitClick?: () => void;
  onShareComplete?: (user: User_InvitePortalClientMutation) => void;
};

type FormShareValuesType = {
  estateSharePermission: EstateSharePermission;
};

const schema = yup.object().shape({
  estateSharePermission: yup.string().required('Required'),
});

const estateSharePermissionSelectOptions = [
  { label: 'Limited Access', value: EstateSharePermission.Limited },
  { label: 'Unlimited Access (Coming Soon)', value: EstateSharePermission.Unlimited },
];

const ClientShareModal = ({
  estateId,
  estateName,
  clientEmailAddress,
  title,
  showModal,
  setShowModal,
  onExitClick,
  onShareComplete = () => {},
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<FormShareValuesType>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      estateSharePermission: EstateSharePermission.Limited,
    },
  });

  const estateSharePermission = watch('estateSharePermission');

  const [invitePortalClient] = useUser_InvitePortalClientMutation({
    onCompleted: onShareComplete,
    onError: (e) => {
      showAlert({
        message: 'Error inviting portal client: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onSubmit = async (values: FormShareValuesType) => {
    const { estateSharePermission } = values;

    if (estateSharePermission === EstateSharePermission.Unlimited) {
      showAlert({
        message: 'Unlimited is coming soon.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    await invitePortalClient({
      variables: {
        input: {
          portalId,
          estateId,
          email: clientEmailAddress,
          estateSharePermission,
        },
      },
    });
    reset();
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={() => {
        setShowModal(false);
        onExitClick && onExitClick();
      }}
      showModal={showModal}
      showActions={false}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.content}>
            <Typography className={classes.title}>{title || `Share ${estateName}`}</Typography>
            <div className={classes.selectContainer}>
              <Typography variant="caption">
                Client email: <strong>{clientEmailAddress}</strong>
              </Typography>
              <Controller
                name="estateSharePermission"
                control={control}
                render={({ field: { ref: _, ...field } }) => (
                  <FormSelectField
                    {...field}
                    errorMessage={errors.estateSharePermission?.message}
                    options={estateSharePermissionSelectOptions}
                    label="Manage access"
                    placeholder="Select access level"
                    className={classes.selectInput}
                  />
                )}
              />
              {estateSharePermission === EstateSharePermission.Limited && (
                <>
                  <Typography variant="caption">
                    My client can view the dashboard (task list), and access and upload documents to
                    the vault.
                  </Typography>
                </>
              )}
              {estateSharePermission === EstateSharePermission.Unlimited && (
                <>
                  <div className={classes.comingSoonBadge}>
                    <Chip label="Coming Soon" color={ChipDefaultColor.BLACK_BACKGROUND} />
                  </div>
                  <Typography variant="caption">
                    My client can do all of the actions from <strong>Limited Access</strong>, PLUS
                    purchase tasks bundles to assign tasks directly to Trustate.
                  </Typography>
                </>
              )}
            </div>
            <Button
              type="submit"
              variant="outlined"
              className={classes.shareButton}
              disabled={isSubmitting || estateSharePermission === EstateSharePermission.Unlimited}
            >
              Share
            </Button>
          </div>
        </form>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default ClientShareModal;
